import { COMPANY_LABELS, type Company } from '@nova/shared';
import {
  ChevronDown,
  Eye,
  EyeOff,
  KeyRound,
  LogOut,
  MoreHorizontal,
  Moon,
  Monitor,
  Plus,
  Sun,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider.js';
import { Button } from '../components/Button.js';
import { NovaWordmark, type SystemStatus } from '../components/NovaOrb.js';
import { cn } from '../lib/cn.js';
import { useNewContent } from '../features/content/NewContentProvider.js';
import { useFilter } from './FilterProvider.js';
import { useTheme } from './ThemeProvider.js';
import { groupNavItems, NAV_ITEMS, type NavItem } from './navigation.js';
import { ProposalReviewBadge } from '../features/content/ProposalReviewProvider.js';
import { ChangePasswordDialog } from '../features/auth/ChangePasswordDialog.js';
import { useSystemStatus } from './useSystemStatus.js';

const COMPANY_OPTIONS: (Company | 'ALLE')[] = ['ALLE', 'SPAMA', 'PROKOM'];

export function AppShell() {
  const { user, hasModule } = useAuth();
  const status = useSystemStatus();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => setMoreOpen(false), [location.pathname]);

  const items = NAV_ITEMS.filter(
    (item) =>
      (!item.adminOnly || user?.role === 'ADMIN') &&
      (!item.superadminOnly || user?.isSuperadmin) &&
      (!item.module || hasModule(item.module)),
  );
  const mobilePrimary = items.filter((i) => i.mobilePrimary);
  const mobileMore = items.filter((i) => !i.mobilePrimary);

  return (
    <div className="flex min-h-dvh">
      <IconRail status={status} />

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar status={status} />

        <main id="hovedinnhold" className="min-w-0 flex-1 px-4 pb-24 pt-4 md:px-6 md:pb-8 lg:px-8">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom navigation — Status, Plan, Varsler, Mer (06). */}
      <nav
        aria-label="Hovednavigasjon (mobil)"
        className="border-hairline bg-panel/90 backdrop-blur-glass fixed inset-x-0 bottom-0 z-30 flex border-t md:hidden"
      >
        {mobilePrimary.map(({ to, mobileLabel, label, Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) =>
              cn(
                'flex flex-1 flex-col items-center gap-1 py-2.5 text-[11px] transition-colors',
                isActive ? 'text-accent' : 'text-ink-soft',
              )
            }
          >
            <Icon size={19} strokeWidth={1.8} aria-hidden />
            {mobileLabel ?? label}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          aria-expanded={moreOpen}
          className={cn(
            'flex flex-1 flex-col items-center gap-1 py-2.5 text-[11px] transition-colors',
            moreOpen ? 'text-accent' : 'text-ink-soft',
          )}
        >
          {moreOpen ? <X size={19} aria-hidden /> : <MoreHorizontal size={19} aria-hidden />}
          Mer
        </button>
      </nav>

      {moreOpen && (
        <div className="border-hairline bg-panel/95 backdrop-blur-glass fixed inset-x-0 bottom-[57px] z-30 max-h-[70dvh] overflow-y-auto border-t p-2 md:hidden">
          {(() => {
            const { top, sections } = groupNavItems(mobileMore);
            const renderMobile = (item: NavItem) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-3 py-3 text-sm',
                    isActive ? 'bg-raised text-accent' : 'text-ink',
                  )
                }
              >
                <item.Icon size={18} strokeWidth={1.8} aria-hidden />
                {item.label}
              </NavLink>
            );
            return (
              <>
                {top.map(renderMobile)}
                {sections.map((group) => (
                  <div key={group.key}>
                    <p className="text-micro text-ink-soft/50 px-3 pb-1 pt-3 uppercase">
                      {group.label}
                    </p>
                    {group.items.map(renderMobile)}
                  </div>
                ))}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function IconRail({ status }: { status: SystemStatus }) {
  const { user, hasModule } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const items = NAV_ITEMS.filter(
    (item) =>
      (!item.adminOnly || user?.role === 'ADMIN') &&
      (!item.superadminOnly || user?.isSuperadmin) &&
      (!item.module || hasModule(item.module)),
  );
  const { top, sections } = groupNavItems(items);

  const renderItem = (item: NavItem) => (
    <li key={item.to}>
      <NavLink
        to={item.to}
        end={item.to === '/'}
        className={({ isActive }) =>
          cn(
            'group relative flex h-11 items-center gap-3 overflow-hidden rounded-lg px-3 transition-colors',
            isActive
              ? 'bg-accent/12 text-accent'
              : 'text-ink-soft hover:bg-raised/70 hover:text-ink',
          )
        }
        title={expanded ? undefined : item.label}
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <span
                aria-hidden
                className="bg-accent shadow-glow-sm absolute inset-y-2 left-0 w-0.5 rounded-full"
              />
            )}
            <item.Icon size={19} strokeWidth={1.8} className="shrink-0" aria-hidden />
            <span
              className={cn(
                'whitespace-nowrap text-sm transition-opacity duration-150',
                expanded ? 'opacity-100' : 'pointer-events-none opacity-0',
              )}
            >
              {item.label}
            </span>
          </>
        )}
      </NavLink>
    </li>
  );

  return (
    <nav
      aria-label="Hovednavigasjon"
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      className={cn(
        'border-hairline bg-panel/50 backdrop-blur-glass sticky top-0 hidden h-dvh shrink-0 flex-col border-r transition-[width] duration-200 md:flex',
        expanded ? 'w-56' : 'w-16',
      )}
    >
      <div className="flex h-16 items-center overflow-hidden px-4">
        {expanded ? <NovaWordmark status={status} /> : <NovaWordmark status={status} compact />}
      </div>

      <ul className="flex flex-1 flex-col gap-1 overflow-y-auto px-2">
        {top.map(renderItem)}
        {sections.map((group) => (
          <li key={group.key} className="contents">
            {/* A labelled header when expanded; a thin rule to keep groups
                separated while the rail is collapsed to icons. */}
            <div className="px-3 pb-1 pt-3" aria-hidden>
              {expanded ? (
                <span className="text-micro text-ink-soft/50 uppercase">{group.label}</span>
              ) : (
                <span className="border-hairline block border-t" />
              )}
            </div>
            <ul className="flex flex-col gap-1">{group.items.map(renderItem)}</ul>
          </li>
        ))}
      </ul>

      <p
        className={cn(
          'text-micro text-ink-soft/60 px-4 pb-4 uppercase transition-opacity',
          expanded ? 'opacity-100' : 'opacity-0',
        )}
      >
        Nova v0.1
      </p>
    </nav>
  );
}

function TopBar({ status }: { status: SystemStatus }) {
  const { user, logout, demoMode } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { company, setCompany, areaId, setAreaId, visibleAreas, hideDemo, setHideDemo } =
    useFilter();
  const { open: openNewContent } = useNewContent();
  const [menuOpen, setMenuOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onClick(event: MouseEvent) {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  return (
    <header className="border-hairline bg-base/80 backdrop-blur-glass sticky top-0 z-20 flex h-16 items-center gap-3 border-b px-4 md:px-6 lg:px-8">
      <div className="md:hidden">
        <NovaWordmark status={status} compact />
      </div>

      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto">
        <div className="border-hairline bg-panel/60 flex shrink-0 rounded-lg border p-0.5">
          {COMPANY_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setCompany(option)}
              className={cn(
                'rounded-md px-2.5 py-1.5 text-xs transition-colors',
                company === option ? 'bg-accent/15 text-accent' : 'text-ink-soft hover:text-ink',
              )}
            >
              {option === 'ALLE' ? 'Alle' : COMPANY_LABELS[option]}
            </button>
          ))}
        </div>

        <div className="relative shrink-0">
          <select
            value={areaId ?? ''}
            onChange={(e) => setAreaId(e.target.value || null)}
            aria-label="Filtrer på område"
            className="border-hairline bg-panel/60 text-ink hover:bg-panel h-9 appearance-none rounded-lg border pl-3 pr-8 text-xs transition"
          >
            <option value="">Alle områder</option>
            {visibleAreas.map((area) => (
              <option key={area.id} value={area.id}>
                {area.name}
              </option>
            ))}
          </select>
          <ChevronDown
            size={14}
            className="text-ink-soft pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2"
            aria-hidden
          />
        </div>

        {/* One switch that takes the fabricated seed data off every surface at
            once — the only way to see what Nova has actually done (07 — 2.1.8).
            Only shown in a demo environment; production has no seed data to hide. */}
        {demoMode && (
          <button
            type="button"
            onClick={() => setHideDemo(!hideDemo)}
            aria-pressed={hideDemo}
            // The label is hidden below `sm`, so the button needs a name of its
            // own to stay reachable on a phone.
            aria-label={hideDemo ? 'Demo-data skjult' : 'Skjul demo-data'}
            title="Skjuler saker, kjøringer, varsler og tall som kommer fra demo-dataene"
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition',
              hideDemo
                ? 'border-accent/40 bg-accent/12 text-accent'
                : 'border-hairline bg-panel/60 text-ink-soft hover:text-ink',
            )}
          >
            {hideDemo ? <EyeOff size={12} aria-hidden /> : <Eye size={12} aria-hidden />}
            <span className="hidden sm:inline">
              {hideDemo ? 'Demo-data skjult' : 'Skjul demo-data'}
            </span>
          </button>
        )}

        {demoMode && (
          <span className="border-warning/30 bg-warning/10 text-warning hidden shrink-0 rounded-full border px-2.5 py-1 text-[11px] xl:inline">
            Demo-modus — ingenting sendes eksternt
          </span>
        )}
      </div>

      {user?.role !== 'VIEWER' && (
        <Button
          variant="primary"
          size="sm"
          icon={<Plus size={14} aria-hidden />}
          onClick={openNewContent}
          className="shrink-0"
        >
          <span className="hidden sm:inline">Nytt innlegg</span>
        </Button>
      )}

      {user?.role !== 'VIEWER' && <ProposalReviewBadge />}

      <div className="relative shrink-0" ref={menuRef}>
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Brukermeny"
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          className="border-hairline bg-panel/60 hover:bg-panel flex h-9 items-center gap-2 rounded-lg border pl-1.5 pr-2.5 transition"
        >
          <span className="bg-accent/15 text-accent flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold">
            {initials(user?.name ?? '')}
          </span>
          <span className="text-ink hidden max-w-[10rem] truncate text-xs sm:inline">
            {user?.name}
          </span>
          <ChevronDown size={13} className="text-ink-soft" aria-hidden />
        </button>

        {menuOpen && (
          <div
            role="menu"
            className="menu-surface animate-fade-up absolute right-0 top-11 w-60 p-1.5"
          >
            <div className="border-hairline border-b px-3 py-2">
              <p className="text-ink truncate text-sm">{user?.name}</p>
              <p className="text-ink-soft truncate text-xs">{user?.title ?? user?.email}</p>
            </div>
            <button
              type="button"
              role="menuitem"
              onClick={toggleTheme}
              className="text-ink-soft hover:bg-raised hover:text-ink mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition"
            >
              {theme === 'light' ? (
                <Sun size={15} aria-hidden />
              ) : theme === 'dark' ? (
                <Moon size={15} aria-hidden />
              ) : (
                <Monitor size={15} aria-hidden />
              )}
              Tema: {theme === 'light' ? 'Lyst' : theme === 'dark' ? 'Mørkt' : 'System'}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                setPasswordOpen(true);
              }}
              className="text-ink-soft hover:bg-raised hover:text-ink mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition"
            >
              <KeyRound size={15} aria-hidden />
              Bytt passord
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => void logout()}
              className="text-ink-soft hover:bg-raised hover:text-ink mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition"
            >
              <LogOut size={15} aria-hidden />
              Logg ut
            </button>
          </div>
        )}

        <ChangePasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
      </div>
    </header>
  );
}

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
